// Base
import { BaseApi } from 'api/base.api';

// Interface
import {
  IGalleryApi,
  IGalleryApiRequest,
  IGalleryApiGetAllAttrs,
  IGalleryApiGetAllResponse,
  IGalleryApiGetAttrs,
  IGalleryApiGetResponse,
  IGalleryApiSaveAttrs,
  IGalleryApiSaveResponse,
  IGalleryApiDeleteImageAttrs,
  IGalleryApiDeleteImageResponse,
  IGalleryApiGetGalleryImagesAttrs,
  IGalleryApiGetGalleryImagesResponse,
  IGalleryApiUpdateImageAttrs,
  IGalleryApiUpdateImageResponse,
} from 'interfaces/back-office/content-management/gallery/gallery/gallery-api.interface';
import { IBaseApiRequestPayload } from 'interfaces/api';

// Constant
import { ApiConstant } from 'constant';

// Utils
import { axios_renderUrl } from 'utils';

// eslint-disable-next-line
import { GALLERIES, GALLERY, GALLERY_IMAGES } from 'constant/content-management/gallery/gallery.constant';

export class GalleryApi extends BaseApi<IGalleryApi> implements IGalleryApiRequest {
  readonly url = ApiConstant.CONTENT_GALLERY_URL;
  private galleryItemsUrl = axios_renderUrl(`${this.url}/items` as any, ApiConstant.BACK_OFFICE);
  public galleryContentUrl = axios_renderUrl(`${this.url}/content` as any, ApiConstant.BACK_OFFICE);

  /**
   * @description Get galleries
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IGalleryApiGetAllAttrs} payload
   *
   * @return {Promise<IGalleryApiGetAllResponse>} Promise<IGalleryApiGetAllResponse>
   */
  async gallery_getGalleries(payload: IGalleryApiGetAllAttrs): Promise<IGalleryApiGetAllResponse> {
    try {
      const response = await this.POST(`${this.galleryItemsUrl}/list`, payload.body, { ...payload.requestConfig });

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * @description Get gallery
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IGalleryApiGetAttrs} payload
   *
   * @return {Promise<IGalleryApiGetResponse>} Promise<IGalleryApiGetResponse>
   */
  async gallery_getGallery(payload: IGalleryApiGetAttrs): Promise<IGalleryApiGetResponse> {
    try {
      const response = await this.POST(`${this.galleryItemsUrl}/${payload.params.id}`, {}, { ...payload.requestConfig });

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * @description Save gallery
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IGalleryApiSaveAttrs} payload
   *
   * @return {Promise<IGalleryApiSaveResponse>} Promise<IGalleryApiSaveResponse>
   */
  async gallery_saveGallery(payload: IGalleryApiSaveAttrs): Promise<IGalleryApiSaveResponse> {
    try {
      const requestPayload: IBaseApiRequestPayload = {
        method: 'POST',
        url: payload.params?.id ? `${this.galleryItemsUrl}/update/${payload.params.id}` : `${this.url}/create`,
      };
      const response = await this[requestPayload.method](requestPayload.url, payload.body, { ...payload.requestConfig });

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * @description Update image gallery
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IGalleryApiUpdateImageAttrs} payload
   *
   * @return {Promise<IGalleryApiUpdateImageResponse>} Promise<IGalleryApiUpdateImageResponse>
   */
  async gallery_updateImage(payload: IGalleryApiUpdateImageAttrs): Promise<IGalleryApiUpdateImageResponse> {
    try {
      let formData = new FormData();
      formData.append('image', payload.body.image);

      const response = await this.POST(`${this.galleryContentUrl}/update/${payload.params.imageId}`, formData, {
        ...payload.requestConfig,
      });

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * @description Delete image gallery
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IGalleryApiSaveAttrs} payload
   *
   * @return {Promise<IGalleryApiSaveResponse>} Promise<IGalleryApiSaveResponse>
   */
  async gallery_deleteImage(payload: IGalleryApiDeleteImageAttrs): Promise<IGalleryApiDeleteImageResponse> {
    try {
      const response = await this.POST(
        `${this.galleryContentUrl}/delete/${payload.params.imageId}`,
        {},
        { ...payload.requestConfig },
      );

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * @description Get images by gallery id
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IGalleryApiSaveAttrs} payload
   *
   * @return {Promise<IGalleryApiSaveResponse>} Promise<IGalleryApiSaveResponse>
   */
  async gallery_getGalleryImages(payload: IGalleryApiGetGalleryImagesAttrs): Promise<IGalleryApiGetGalleryImagesResponse> {
    try {
      const response = await this.POST(
        `${this.galleryContentUrl}/list/${payload.params.galleryId}`,
        { ...payload.body },
        { ...payload.requestConfig },
      );

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    }
  }
}
